import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useSidePanel } from "../context/SidePanelContext";
import NotesTab from "../components/NotesTab";
import {
  saveFileToStorage,
  getAllFiles,
  deleteFileFromStorage,
  clearAllFiles,
  base64ToFile,
} from "../utils/fileStorage";
import {
  FileText,
  Upload,
  Trash2,
  AlertCircle,
  Search,
  Grid,
  List,
  ChevronLeft,
  ChevronRight,
} from "react-feather";
import styles from "../styles/Resources.module.css";

const RESOURCE_TYPES = [
  {
    title: "Documents",
    description: "Upload and analyze PDFs, Word docs, and text files",
    icon: <FileText size={24} />,
    number: "01",
    metric: "Unlimited storage",
  },
  {
    title: "Quizzes",
    description: "Generate interactive quizzes from your documents",
    icon: <FileText size={24} />,
    number: "02",
    metric: "AI-powered",
  },
  {
    title: "Flashcards",
    description: "Create flashcards for effective study sessions",
    icon: <FileText size={24} />,
    number: "03",
    metric: "Smart learning",
  },
  {
    title: "Summaries",
    description: "Get concise summaries of complex documents",
    icon: <FileText size={24} />,
    number: "04",
    metric: "Quick insights",
  },
];

const Resources = () => {
  const { openFilePanel, openComponentTab } = useSidePanel();

  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [resources, setResources] = useState([]);
  const [error, setError] = useState(null);
  const [storageUsage, setStorageUsage] = useState({
    used: 0,
    total: 3.5 * 1024 * 1024,
  }); // ~3.5MB effective limit accounting for browser overhead
  const carouselRef = useRef(null);
  const libraryCarouselRef = useRef(null);

  const loadResources = useCallback(() => {
    try {
      const allFiles = getAllFiles();
      setResources(allFiles);
    } catch (error) {
      console.error("Error loading resources:", error);
      setError("Failed to load resources");
    }
  }, []);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  useEffect(() => {
    const handleFilesUpdated = () => {
      loadResources();
    };

    window.addEventListener("filesUpdated", handleFilesUpdated);
    return () => window.removeEventListener("filesUpdated", handleFilesUpdated);
  }, [loadResources]);

  // Calculate storage usage whenever resources change
  useEffect(() => {
    if (resources.length > 0) {
      // Calculate actual storage usage including base64 overhead
      const base64Overhead = 1.37; // Base64 adds ~37% overhead
      const jsonOverhead = 1.2; // JSON adds ~20% overhead

      const totalUsed = resources.reduce((total, file) => {
        const originalSize = file.size || 0;
        const base64Size = originalSize * base64Overhead;
        const jsonSize = base64Size * jsonOverhead;
        return total + jsonSize;
      }, 0);

      setStorageUsage((prev) => ({ ...prev, used: totalUsed }));
    } else {
      setStorageUsage((prev) => ({ ...prev, used: 0 }));
    }
  }, [resources]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAction = useCallback(
    (action, resource) => {
      switch (action) {
        case "delete":
          try {
            deleteFileFromStorage(resource.id);
            loadResources();
          } catch (error) {
            setError("Failed to delete resource");
            console.error("Error deleting resource:", error);
          }
          break;
        case "openSide":
          const isDocFile =
            resource.type.includes("msword") ||
            resource.type.includes("officedocument.wordprocessing");

          if (isDocFile && resource.textContent) {
            // DOC/DOCX files - display extracted text content as a text file
            const textBlob = new Blob([resource.textContent], {
              type: "text/plain",
            });
            const textFile = new File([textBlob], `${resource.name}.txt`, {
              type: "text/plain",
            });
            openFilePanel(textFile);
          } else if (isDocFile) {
            // DOC/DOCX files without text extraction - create fallback text file
            const fallbackText = `Document: ${resource.name}

File uploaded successfully.
Size: ${formatFileSize(resource.size)}
Uploaded: ${new Date(resource.createdAt).toLocaleDateString()}

Text content extraction is processing or not available.`;
            const textBlob = new Blob([fallbackText], { type: "text/plain" });
            const textFile = new File([textBlob], `${resource.name}.txt`, {
              type: "text/plain",
            });
            openFilePanel(textFile);
          } else {
            // All other file types (PDF, TXT, etc.) - open as files
            const file = base64ToFile(
              resource.content,
              resource.name,
              resource.type
            );
            openFilePanel(file);
          }

          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("closeSidebar"));
          }, 100);
          break;
        default:
          break;
      }
    },
    [loadResources, openFilePanel]
  );

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearAllFiles = () => {
    try {
      clearAllFiles();
      loadResources();
    } catch (error) {
      console.error("Error clearing all files:", error);
      setError("Failed to clear all files");
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];

        saveFileToStorage(file, base64)
          .then((newResource) => {
            setResources((prev) => [...prev, newResource]);
            window.dispatchEvent(new CustomEvent("filesUpdated"));
          })
          .catch((error) => {
            setError(error.message);
            console.error("Error saving file:", error);
          });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "text/plain": [".txt"],
    },
  });

  const filteredResources = resources.filter((resource) =>
    resource.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const scrollLibrary = (direction) => {
    if (libraryCarouselRef.current) {
      const scrollAmount = direction === "left" ? -300 : 300;
      libraryCarouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const scrollResourceTypes = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = direction === "left" ? -300 : 300;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <div className={styles.resourcesContainer}>
      <div className={styles.content}>
        <header className={styles.header}>
          <h1 className={styles.title}>My Resources</h1>
          <p className={styles.subtitle}>
            Manage your documents, quizzes, and study materials
          </p>
        </header>

        {/* Quick actions */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            margin: "8px 0 16px 0",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => {
              // Dispatch event to open Document Library
              window.dispatchEvent(new CustomEvent("openDocumentLibrary"));
            }}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "2px solid #3b82f6",
              background:
                "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05))",
              color: "#3b82f6",
              cursor: "pointer",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
            title="Open Document Library - Upload, manage, and generate from documents"
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                "linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(59, 130, 246, 0.15))";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05))";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <FileText size={18} />
            📚 Document Library (Recommended)
          </button>
          <button
            onClick={() => openComponentTab(<NotesTab />, "Notes")}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "var(--muted)",
              color: "var(--foreground)",
              cursor: "pointer",
            }}
            title="Open Chat Notes"
          >
            Chat Notes
          </button>
        </div>

        <div className={styles.disclaimerSmall}>
          <AlertCircle size={14} />
          <span>
            Large files use temporary storage and may need to be re-uploaded
            after session
          </span>
        </div>

        <div className={styles.storageProgress}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${Math.min(
                  (storageUsage.used / storageUsage.total) * 100,
                  100
                )}%`,
                backgroundColor:
                  storageUsage.used > storageUsage.total * 0.8
                    ? "#ef4444"
                    : storageUsage.used > storageUsage.total * 0.6
                    ? "#f59e0b"
                    : "#10b981",
              }}
            />
          </div>
          <span className={styles.progressText}>
            {storageUsage.used > storageUsage.total
              ? `${
                  Math.round((storageUsage.used / 1024 / 1024) * 100) / 100
                }MB used (over limit)`
              : `${
                  Math.round((storageUsage.used / 1024 / 1024) * 100) / 100
                }MB / 3.5MB`}
          </span>
          {storageUsage.used > storageUsage.total && (
            <span className={styles.storageWarning}>
              Storage full - consider clearing some files
            </span>
          )}
        </div>

        <div
          {...getRootProps()}
          className={`${styles.uploadZone} ${
            isDragActive ? styles.dragOver : ""
          }`}
        >
          <input {...getInputProps()} />
          <div className={styles.uploadIcon}>
            <Upload size={48} />
          </div>
          <p className={styles.uploadText}>
            {isDragActive
              ? "Drop your files here"
              : "Drag and drop your files here"}
          </p>
          <p className={styles.uploadSubtext}>
            or click to select files (PDF, DOC, DOCX, TXT)
          </p>
        </div>

        {error && (
          <div className={styles.error}>
            <AlertCircle size={20} />
            <p>{error}</p>
          </div>
        )}

        <div className={styles.resourceControls}>
          <div className={styles.searchBar}>
            <Search size={18} />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          <div className={styles.viewControls}>
            <button
              className={`${styles.viewButton} ${
                viewMode === "carousel" ? styles.active : ""
              }`}
              onClick={() =>
                setViewMode(viewMode === "carousel" ? "list" : "carousel")
              }
              title={
                viewMode === "carousel"
                  ? "Switch to list view"
                  : "Switch to carousel view"
              }
            >
              {viewMode === "carousel" ? (
                <List size={16} />
              ) : (
                <Grid size={16} />
              )}
            </button>
          </div>
          {resources.length > 0 && (
            <button
              className={styles.clearAllButton}
              onClick={handleClearAllFiles}
            >
              <Trash2 size={16} />
              Clear All Files
            </button>
          )}
        </div>

        {/* Library Files */}
        <section className={styles.librarySection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>My Library</h2>
          </div>

          {filteredResources.length === 0 ? (
            <div className={styles.emptyState}>
              <FileText size={48} style={{ marginBottom: "1rem" }} />
              <h3 style={{ margin: "0 0 0.5rem 0" }}>No resources yet</h3>
              <p style={{ margin: 0, textAlign: "center" }}>
                Upload your first document to get started
              </p>
            </div>
          ) : (
            <>
              {viewMode === "list" ? (
                <div className={styles.scrollableListView}>
                  {filteredResources.map((resource) => (
                    <div
                      key={resource.id}
                      className={styles.listItem}
                      onClick={() => handleAction("openSide", resource)}
                      title={`${resource.name} - ${formatFileSize(
                        resource.size
                      )}${resource.isSessionFile ? " (session file)" : ""}`}
                    >
                      <div className={styles.listItemIcon}>
                        <FileText size={16} />
                      </div>
                      <div className={styles.listItemContent}>
                        <span className={styles.listItemTitle}>
                          {resource.name.length > 50
                            ? `${resource.name.substring(0, 47)}...`
                            : resource.name}
                        </span>
                        <span className={styles.listItemMeta}>
                          {formatFileSize(resource.size)}
                          {resource.isSessionFile && (
                            <span className={styles.sessionBadge}>session</span>
                          )}
                        </span>
                      </div>
                      <button
                        className={styles.listActionButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction("delete", resource);
                        }}
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div
                    className={styles.libraryCarousel}
                    ref={libraryCarouselRef}
                  >
                    {filteredResources.map((resource) => (
                      <div
                        key={resource.id}
                        className={styles.libraryCardCompact}
                        onClick={() => handleAction("openSide", resource)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className={styles.filePreview}>
                          {resource.type?.startsWith("image/") ? (
                            <img
                              src={`data:${resource.type};base64,${resource.content}`}
                              alt={resource.name}
                              className={styles.previewImage}
                            />
                          ) : resource.type?.includes("text") ||
                            resource.name?.includes(".txt") ||
                            resource.name?.includes(".md") ||
                            resource.name?.includes(".js") ||
                            resource.name?.includes(".py") ||
                            resource.name?.includes(".css") ||
                            resource.name?.includes(".html") ? (
                            <div className={styles.textPreview}>
                              <pre className={styles.textContent}>
                                {resource.content && resource.content.length > 0
                                  ? resource.content.length > 200
                                    ? resource.content.substring(0, 200) + "..."
                                    : resource.content
                                  : "Text file"}
                              </pre>
                            </div>
                          ) : resource.type?.includes("pdf") ? (
                            <div className={styles.fileIcon}>
                              <FileText
                                size={32}
                                style={{ color: "#e74c3c" }}
                              />
                            </div>
                          ) : resource.name?.includes(".doc") ||
                            resource.name?.includes(".docx") ? (
                            <div className={styles.textPreview}>
                              <div className={styles.wordPreview}>
                                <FileText
                                  size={24}
                                  style={{
                                    color: "#2b579a",
                                    marginBottom: "4px",
                                  }}
                                />
                                <span className={styles.previewText}>
                                  {resource.name}
                                </span>
                                <span className={styles.previewHint}>
                                  {resource.content &&
                                  resource.content.length > 0
                                    ? resource.content.length > 80
                                      ? resource.content.substring(0, 80) +
                                        "..."
                                      : resource.content
                                    : "Document content"}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.fileIcon}>
                              <FileText
                                size={32}
                                style={{
                                  color: "var(--foreground)",
                                  opacity: 0.6,
                                }}
                              />
                            </div>
                          )}
                        </div>
                        <div className={styles.libraryCardContent}>
                          <h4
                            className={styles.libraryCardTitle}
                            title={resource.name}
                          >
                            {resource.name.length > 25
                              ? `${resource.name.substring(0, 22)}...`
                              : resource.name}
                          </h4>
                          <p className={styles.libraryCardSize}>
                            {formatFileSize(resource.size)}
                          </p>
                        </div>
                        <button
                          className={styles.libraryActionButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction("delete", resource);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className={styles.carouselControlsBottom}>
                    <button
                      className={styles.carouselButton}
                      onClick={() => scrollLibrary("left")}
                      aria-label="Scroll library left"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      className={styles.carouselButton}
                      onClick={() => scrollLibrary("right")}
                      aria-label="Scroll library right"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </section>

        {/* Resource Types */}
        <section className={styles.resourceTypes}>
          <div className={styles.carouselHeader}>
            <h2 className={styles.sectionTitle}>Resource Types</h2>
          </div>
          <div className={styles.typeCarousel} ref={carouselRef}>
            {RESOURCE_TYPES.map((type, index) => (
              <div
                key={type.title}
                className={styles.typeCard}
                style={{ animationDelay: `${0.1 * (index + 2)}s` }}
              >
                <div className={styles.typeNumber}>{type.number}</div>
                <div className={styles.typeIcon}>{type.icon}</div>
                <h3 className={styles.typeTitle}>{type.title}</h3>
                <p className={styles.typeDescription}>{type.description}</p>
                <span className={styles.typeMetric}>{type.metric}</span>
              </div>
            ))}
          </div>
          <div className={styles.carouselControlsBottom}>
            <button
              className={styles.carouselButton}
              onClick={() => scrollResourceTypes("left")}
              aria-label="Scroll resource types left"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className={styles.carouselButton}
              onClick={() => scrollResourceTypes("right")}
              aria-label="Scroll resource types right"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Resources;
